import type {SearchParams} from 'src/typings';
import type {SearchManager} from 'src/features/search/search-manager/search-manager';
import {exists} from 'shared/utils/custom-utils';
import type {Pagination, PageRequest} from 'src/features/search/search-manager/pagination';

export class SearchManagerConnectableProxy<ITEM, PARAMS extends SearchParams = SearchParams> {
    private searchManager: SearchManager<ITEM, PARAMS> | null = null;
    
    public connect(searchManager: SearchManager<ITEM, PARAMS>) {
        this.searchManager = searchManager;
    }

    public isConnected(): boolean {
        return exists(this.searchManager);
    }

    public assertIsConnected() {
        if (!this.isConnected()) {
            throw new Error('SearchManagerConnectableProxy: SearchManager is not connected!');
        }
    }

    public getPagination(): Pagination<ITEM> {
        this.assertIsConnected();
        return this.searchManager.getPagination();
    }

    public requestPage(pageRequest: PageRequest): void {
        this.assertIsConnected();
        return this.searchManager.requestPage(pageRequest);
    }

    public requestPageReload(pageNumber: number): void {
        this.assertIsConnected();
        return this.searchManager.requestPageReload(pageNumber);
    }

    public reset(): void {
        this.assertIsConnected();
        return this.searchManager.reset();
    }

    public newSearch(): void {
        this.assertIsConnected();
        return this.searchManager.newSearch();
    }

    public newSearchWithParams(newParams: PARAMS) {
        this.assertIsConnected();
        return this.searchManager.newSearchWithParams(newParams);
    }

    public newSearchWithPartialParams(partialNewParams: Partial<PARAMS>) {
        this.assertIsConnected();
        return this.searchManager.newSearchWithPartialParams(partialNewParams)
    }

    public refreshSearch(): void {
        this.assertIsConnected();
        return this.searchManager.refreshSearch();
    }

    public getStaticParams(): Readonly<Partial<PARAMS>> {
        this.assertIsConnected();
        return this.searchManager.getStaticParams();
    }

    public getFrontendParams(): Readonly<Partial<PARAMS>> {
        this.assertIsConnected();
        return this.searchManager.getFrontendParams();
    }

    public setFrontendParams(params: Partial<PARAMS>): void {
        this.assertIsConnected();
        return this.searchManager.setFrontendParams(params);
    }

    public getCompleteParams(): Readonly<Partial<PARAMS>> {
        this.assertIsConnected();
        return this.searchManager.getCompleteParams();
    }
}